// This file has not properly been working since the backend change. Staff can access all pages manually with route, the redirect was disabled by Zach. Added to bug tracker.

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { lotService } from '../utils/api';
import { isSuperadminModeEnabled, SUPERADMIN_MODE_EVENT } from '../components/SuperadminHeader';

interface LotRoleRouteProps {
    children: React.ReactNode;
    allowedRoles: string[]; // Array of roles that can access this route (e.g., ['owner', 'operator'])
}

const LotRoleRoute: React.FC<LotRoleRouteProps> = ({ children, allowedRoles }) => {
    const { lotId } = useParams<{ lotId: string }>();
    const navigate = useNavigate();
    const { user } = useUser(); // Get the current user from context
    const [userRole, setUserRole] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [hasAccess, setHasAccess] = useState<boolean>(false);
    const [isSuperadminOverride, setIsSuperadminOverride] = useState<boolean>(() => isSuperadminModeEnabled());

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const handleModeChange: EventListener = () => {
            setIsSuperadminOverride(isSuperadminModeEnabled());
        };

        window.addEventListener(SUPERADMIN_MODE_EVENT, handleModeChange);
        window.addEventListener('storage', handleModeChange);

        return () => {
            window.removeEventListener(SUPERADMIN_MODE_EVENT, handleModeChange);
            window.removeEventListener('storage', handleModeChange);
        };
    }, []);

    useEffect(() => {
        const checkRole = async () => {
            if (!lotId || !user) {
                if (!user) {
                    console.log('LotRoleRoute: No user found, redirecting to login');
                    navigate('/login');
                }
                setLoading(false);
                return;
            }

            console.log("LotRoleRoute: Checking role for lotId", lotId, "user", user.userId);

            const accountRole = (user.role || '').toLowerCase();
            const isAccountSuperadmin = accountRole === 'superadmin';
            const superadminModeActive = isAccountSuperadmin && (isSuperadminOverride || isSuperadminModeEnabled());

            if (superadminModeActive) {
                console.log("LotRoleRoute: Operator Superadmin mode detected, granting access");
                setUserRole('superadmin');
                setHasAccess(true);
                setLoading(false);
                return;
            }

            try {
                console.log("LotRoleRoute: Fetching lot data...");
                const lotData = await lotService.getLotById(lotId);
                console.log("LotRoleRoute: Lot data fetched:", lotData);

                let role = 'staff'; // Default to staff

                console.log("LotRoleRoute: Checking if user is owner...");
                console.log("LotRoleRoute: lotData.ownerId", lotData?.ownerId);
                console.log("LotRoleRoute: user.userId", user.userId);

                if (lotData && lotData.ownerId === user.userId) {
                    console.log("LotRoleRoute: User is owner");
                    role = 'owner';
                } else {
                    console.log("LotRoleRoute: User is not owner, checking if operator...");
                    const operatorsList = await lotService.getOperators(lotId);
                    console.log("LotRoleRoute: Operators fetched:", operatorsList);
                    const isOperator = operatorsList.some((op: any) => op.userId === user.userId);
                    console.log("LotRoleRoute: Is user an operator?", isOperator);
                    if (isOperator) {
                        role = 'operator';
                    }
                }

                console.log("LotRoleRoute: Determined role:", role);
                setUserRole(role);

                console.log("LotRoleRoute: Allowed roles for this route:", allowedRoles);
                const userIsAllowed = allowedRoles.includes(role);
                console.log("LotRoleRoute: User is allowed?", userIsAllowed);
                setHasAccess(userIsAllowed);

                if (!userIsAllowed) {
                    console.warn(`Access Denied: User ${user.userId} with role ${role} attempted to access route requiring roles ${allowedRoles.join(', ')} for lot ${lotId}`);
                    navigate(`/lot/${lotId}/dashboard`);
                }

            } catch (error) {
                console.error('Error checking user role for route:', error);
                navigate(`/lot/${lotId}/dashboard`);
            } finally {
                setLoading(false);
                console.log("LotRoleRoute: Finished role check.");
            }
        };

        checkRole();
    }, [lotId, user, allowedRoles, navigate, isSuperadminOverride]);

    if (loading || userRole === null) {
        return <div>Loading access permissions...</div>;
    }

    if (hasAccess) {
        return <>{children}</>;
    } else {
        return null;
    }
};

export default LotRoleRoute; 
